import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaClock, FaFilter } from 'react-icons/fa';

const Tours = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const tours = [
    {
      id: 'addis-ababa-city-tour',
      category: "Day Tour",
      title: "Addis Ababa City Tour – Full Day",
      description: "Discover Ethiopia's vibrant capital city, visit the National Museum, Holy Trinity Cathedral, Mercato market, and experience local cuisine.",
      duration: "Full Day (8-10 hours)",
      location: "Addis Ababa",
      price: "On Request",
      image: "/images/addis-tour.jpg",
      type: "day"
    },
    {
      id: 'debre-libanos',
      category: "Day Tour",
      title: "Debre Libanos & Portuguese Bridge – Day Tour",
      description: "Visit the historic Debre Libanos Monastery, explore the Portuguese Bridge, and enjoy breathtaking views of the Blue Nile Gorge.",
      duration: "1 Day",
      location: "Debre Libanos, Oromia",
      price: "On Request",
      image: "/images/debre-libanos.jpg",
      type: "day"
    },
    {
      id: 'lalibela',
      category: "Multi-Day",
      title: "Lalibela Historical Tour – 3 Days",
      description: "Explore the legendary rock-hewn churches of Lalibela, a UNESCO World Heritage Site and spiritual heart of Ethiopian Christianity.",
      duration: "3 Days",
      location: "Lalibela, Northern Ethiopia",
      price: "On Request",
      image: "/images/lalibela.jpg",
      type: "multi"
    },
    {
      id: 'omo-valley',
      category: "Multi-Day",
      title: "Omo Valley Cultural Tour – 5 Days",
      description: "Meet indigenous tribes with ancient traditions still alive today. Visit the Mursi, Hamer, and Karo people.",
      duration: "5 Days",
      location: "Omo Valley",
      price: "On Request",
      image: "/images/omo-valley.jpg",
      type: "multi"
    },
    {
      id: 'danakil',
      category: "Multi-Day",
      title: "Danakil Depression Expedition – 4 Days",
      description: "Witness Earth's most extreme and colorful volcanic terrain, including the Dallol hydrothermal fields and Erta Ale volcano.",
      duration: "4 Days",
      location: "Danakil Depression",
      price: "On Request",
      image: "/images/danakil.jpg",
      type: "multi"
    },
    {
      id: 'bale-mountains',
      category: "Multi-Day",
      title: "Bale Mountains Trek – 6 Days",
      description: "Trek through dramatic peaks and spot endemic wildlife like the Ethiopian Wolf, Mountain Nyala, and Gelada Baboons.",
      duration: "6 Days",
      location: "Bale Mountains",
      price: "On Request",
      image: "/images/bale.jpg",
      type: "multi"
    },
    {
      id: 'gondar',
      category: "Multi-Day",
      title: "Gondar & Simien Mountains – 4 Days",
      description: "Explore the medieval castles of Gondar and trek through the stunning Simien Mountains, home to Gelada baboons.",
      duration: "4 Days",
      location: "Gondar",
      price: "On Request",
      image: "/images/gondar.jpg",
      type: "multi"
    },
    {
      id: 'axum',
      category: "Multi-Day",
      title: "Axum Archaeological Tour – 2 Days",
      description: "Discover the ancient capital of the Aksumite Empire with its towering obelisks and archaeological sites.",
      duration: "2 Days",
      location: "Axum",
      price: "On Request",
      image: "/images/axum.jpg",
      type: "multi"
    },
    {
      id: 'bahir-dar',
      category: "Multi-Day",
      title: "Bahir Dar & Blue Nile Falls – 2 Days",
      description: "Visit Lake Tana's island monasteries and the spectacular Blue Nile Falls.",
      duration: "2 Days",
      location: "Bahir Dar",
      price: "On Request",
      image: "/images/bahir-dar.jpg",
      type: "multi"
    }
  ];

  const filteredTours = tours.filter(tour => {
    // Filter by type
    if (filter !== 'all' && tour.type !== filter) return false;
    
    // Filter by search term
    if (searchTerm && !tour.title.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !tour.location.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  return (
    <div className="bg-white">
      {/* HERO SECTION */}
      <section 
        className="relative bg-cover bg-center text-white py-32 md:py-40"
        style={{
          backgroundImage: `linear-gradient(rgba(14, 124, 124, 0.85), rgba(11, 94, 94, 0.9)), url('/images/tours-hero.jpg')`,
          marginTop: '-80px',
          paddingTop: '120px'
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">Our Tours</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-white/90">
            Discover Ethiopia with women-led tours designed for authentic experiences
          </p>
        </div>
      </section>
      
      {/* FILTER SECTION */}
      <section className="py-10 bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <FaFilter className="text-gray-400" />
              <span className="font-medium text-gray-700">Filter:</span>
              <div className="flex gap-2">
                <button 
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    filter === 'all' 
                      ? 'bg-[#0e7c7c] text-white' 
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  All Tours
                </button>
                <button 
                  onClick={() => setFilter('day')}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    filter === 'day' 
                      ? 'bg-[#0e7c7c] text-white' 
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Day Tours
                </button>
                <button 
                  onClick={() => setFilter('multi')}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    filter === 'multi' 
                      ? 'bg-[#0e7c7c] text-white' 
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Multi-Day
                </button>
              </div>
            </div>
            
            <div className="w-full md:w-auto">
              <input
                type="text"
                placeholder="Search tours..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0e7c7c] focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* TOURS GRID */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 max-w-7xl">
          {filteredTours.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600 mb-4">No tours match your criteria</p>
              <button 
                onClick={() => { setFilter('all'); setSearchTerm(''); }}
                className="bg-[#0e7c7c] text-white px-6 py-3 rounded-full"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredTours.map((tour, i) => (
                <Link 
                  to={`/tour/${tour.id}`}
                  key={i} 
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                >
                  <div 
                    className="h-56 md:h-64 bg-cover bg-center relative group-hover:scale-105 transition-transform duration-500" 
                    style={{ backgroundImage: `url(${tour.image})` }}
                  >
                    <span className="absolute top-4 left-4 bg-white text-[#0e7c7c] px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wide shadow-md">
                      {tour.category}
                    </span>
                  </div>
                  <div className="p-6 md:p-8">
                    <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-[#0b5e5e] transition-colors">
                      {tour.title}
                    </h4>
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-4 line-clamp-2">{tour.description}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-5">
                      <span className="flex items-center gap-1.5">
                        <FaClock className="text-[#0e7c7c]" /> {tour.duration}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <FaMapMarkerAlt className="text-[#0e7c7c]" /> {tour.location.split(',')[0]}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="font-bold text-gray-900">Price: {tour.price}</span>
                      <span className="bg-[#0e7c7c] hover:bg-[#0b5e5e] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 inline-block">
                        View Tour
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* CUSTOM TOUR CTA */}
      <section className="py-16 bg-[#0e7c7c] text-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Don't See What You're Looking For?</h2>
          <p className="text-lg mb-8 text-white/90">
            We specialize in creating custom itineraries tailored to your interests, schedule, and budget.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-white text-[#0b5e5e] hover:bg-gray-100 px-8 py-4 rounded-full font-semibold text-lg transition-colors duration-200"
            >
              Request Custom Tour
            </Link>
            <a 
              href="https://wa.me/251912345678"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white text-white hover:bg-white hover:text-[#0b5e5e] px-8 py-4 rounded-full font-semibold text-lg transition-colors duration-200"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tours;